import ApiService from './api.service'
import type {
  FinanceBalanceResponse,
  TransactionListResponse,
  TransactionResponse,
  PendingPaymentsResponse,
  PaymentData,
  ExpenseData
} from '@/types/services'

class FinanceService extends ApiService {
  constructor() {
    super({
      showNotifications: true,
      notificationOptions: {
        title: 'Finance Service Error'
      }
    })
  }

  /**
   * Get the financial balance for a project
   */
  async getBalance(projectId: string): Promise<FinanceBalanceResponse | null> {
    return this.get(`/api/project/${projectId}/finance/balance`)
  }

  /**
   * Get all transactions for a project
   */
  async getTransactions(projectId: string): Promise<TransactionListResponse | null> {
    return this.get(`/api/project/${projectId}/finance/transactions`)
  }

  /**
   * Record a new payment for a project
   */
  async recordPayment(projectId: string, paymentData: PaymentData): Promise<TransactionResponse | null> {
    const formData = new FormData()
    formData.append('amount', paymentData.amount.toString())
    formData.append('description', paymentData.description)
    formData.append('paymentType', paymentData.paymentType)
    formData.append('transactionDate', paymentData.transactionDate)
    
    if (paymentData.invoice) {
      formData.append('invoice', paymentData.invoice)
    }
    
    if (paymentData.provisioningRequestId) {
      formData.append('provisioningRequestId', paymentData.provisioningRequestId.toString())
    }

    // Let axios handle the Content-Type header with proper boundary
    return this.post(`/api/project/${projectId}/finance/payments`, formData)
  }

  /**
   * Record a new expense for a project
   */
  async recordExpense(projectId: string, expenseData: ExpenseData): Promise<TransactionResponse | null> {
    const formData = new FormData()
    formData.append('amount', expenseData.amount.toString())
    formData.append('description', expenseData.description)
    formData.append('paymentType', expenseData.paymentType)
    formData.append('intervenantId', expenseData.intervenantId)
    
    if (expenseData.taskId) {
      formData.append('taskId', expenseData.taskId)
    }
    
    formData.append('transactionDate', expenseData.transactionDate)
    
    if (expenseData.invoice) {
      formData.append('invoice', expenseData.invoice)
    }

    // Let axios handle the Content-Type header with proper boundary
    return this.post(`/api/project/${projectId}/finance/expenses`, formData)
  }

  /**
   * Validate a pending payment
   */
  async validatePayment(projectId: string, transactionId: string): Promise<TransactionResponse | null> {
    return this.post(
      `/api/project/${projectId}/finance/payments/${transactionId}/validate`,
      {}
    )
  }

  /**
   * Get pending payments with pagination
   */
  async getPendingPayments(
    projectId: string, 
    page: number = 0, 
    size: number = 10
  ): Promise<PendingPaymentsResponse | null> {
    return this.get(`/api/project/${projectId}/finance/pending-payments`, { page, size })
  }

  /**
   * Edit a pending payment
   */
  async editPendingPayment(
    projectId: string, 
    transactionId: string, 
    paymentData: PaymentData
  ): Promise<TransactionResponse | null> {
    const formData = new FormData()
    formData.append('amount', paymentData.amount.toString())
    formData.append('description', paymentData.description)
    formData.append('paymentType', paymentData.paymentType)
    formData.append('transactionDate', paymentData.transactionDate)
    
    if (paymentData.invoice) {
      formData.append('invoice', paymentData.invoice)
    }

    return this.put(`/api/project/${projectId}/finance/payments/${transactionId}`, formData)
  }

  /**
   * Cancel a pending payment
   */
  async cancelPendingPayment(projectId: string, transactionId: string): Promise<TransactionResponse | null> {
    return this.delete(`/api/project/${projectId}/finance/payments/${transactionId}`)
  }

  /**
   * Edit an expense
   */
  async editExpense(
    projectId: string, 
    transactionId: string, 
    expenseData: ExpenseData
  ): Promise<TransactionResponse | null> {
    const formData = new FormData()
    formData.append('amount', expenseData.amount.toString())
    formData.append('description', expenseData.description)
    formData.append('paymentType', expenseData.paymentType)
    formData.append('intervenantId', expenseData.intervenantId)
    
    if (expenseData.taskId) {
      formData.append('taskId', expenseData.taskId)
    }
    
    formData.append('transactionDate', expenseData.transactionDate)
    
    if (expenseData.invoice) {
      formData.append('invoice', expenseData.invoice)
    }

    return this.put(`/api/project/${projectId}/finance/expenses/${transactionId}`, formData)
  }

  /**
   * Cancel a transaction (expense)
   */
  async cancelTransaction(projectId: string, transactionId: string): Promise<TransactionResponse | null> {
    return this.delete(`/api/project/${projectId}/finance/expenses/${transactionId}`)
  }
}

// Create and export a singleton instance
export default new FinanceService()
